'use client'

import { XIcon } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'

import {
  isRudderstackLoaded,
  subscribeToRudderstackLoaded,
  trackRudderstackEvent,
} from '@/hooks/useRudderstackAnalytics'
import { brand } from '@/utils/brand'

import { cn } from './ui/cn'

const STORAGE_KEY = 'shipgo_modal_shown_v1'
const TRIGGER_PARAM = 'from'
const TRIGGER_VALUE = 'shipgo'
const EVENT_NAME = 'go_modal_shown'

// Env-driven: unset → the "Access It Here" secondary CTA is hidden. GSE-12325
// QA found the previous hard-coded placeholder (shipgo.com/orders) 404s, so
// we no longer ship a default. Product owns the destination; set
// NEXT_PUBLIC_SHIPGO_EXISTING_BOOKING_URL in the brand env when it's known.
// Read at render time so tests can flip it without resetModules gymnastics.
const existingBookingUrl = (): string => process.env.NEXT_PUBLIC_SHIPGO_EXISTING_BOOKING_URL ?? ''

/**
 * Fire go_modal_shown via the project's RudderStack module ref. The SDK is
 * loaded asynchronously after window 'load' by RudderstackAnalyticsBootstrap,
 * which is often AFTER our open-effect runs on narrative pages — a direct
 * trackRudderstackEvent() call would then no-op (the module ref is still
 * null) and the data plane would never see the event. Subscribe to the
 * loaded signal and fire once the SDK is ready; if it's already loaded, fire
 * immediately. Idempotent via the single-shot subscription unsub.
 */
const fireGoModalShownWhenLoaded = (properties: { source: string; path: string }): void => {
  const fire = (): void => {
    trackRudderstackEvent(EVENT_NAME, properties)
  }
  if (isRudderstackLoaded()) {
    fire()
    return
  }
  const unsub = subscribeToRudderstackLoaded(() => {
    unsub()
    fire()
  })
}

/**
 * ShipGo welcome modal (GSE-12387, Kettle narrative-page slice).
 *
 * Fires on first-page-of-session for shipplay traffic redirected from ShipGo
 * (identified by `?from=shipgo`), writes a Rudderstack `go_modal_shown`
 * track event, and persists the dismissal in `localStorage` so subsequent
 * visits (same or later session) don't re-show it. Dismisses via the Start
 * Shipping CTA, backdrop click, or Escape key. Access It Here on the
 * secondary CTA sends the user to shipgo.com/orders to look up an
 * in-flight order.
 *
 * Mount the component anywhere once (currently inside NarrativePage so it
 * scopes to narrative pages like /how-it-works, /faq, /about). It bails
 * silently off-brand, off-trigger, or if already-dismissed.
 */
export const ShipGoWelcomeModal = (): React.ReactElement | null => {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (brand.key !== 'shipplay') return
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.get(TRIGGER_PARAM) !== TRIGGER_VALUE) return
      if (window.localStorage.getItem(STORAGE_KEY)) return
    } catch {
      return
    }
    setIsOpen(true)
    // Mark as shown immediately. The storage key gates both the next mount
    // and the Rudderstack event, so a reload before the user clicks Close
    // (GSE-12325 QA finding) can't re-show the modal nor fire a second
    // go_modal_shown event. The semantic shifted from "dismissed" to
    // "shown" — close-time setItem below becomes an idempotent refresh.
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString())
    } catch {
      /* localStorage may be unavailable (private browsing); fail silently */
    }
    fireGoModalShownWhenLoaded({
      source: 'shipgo',
      path: window.location.pathname,
    })
  }, [])

  const persistAndClose = useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString())
    } catch {
      /* localStorage may be unavailable (private browsing); fail silently */
    }
    setIsOpen(false)
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') persistAndClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [isOpen, persistAndClose])

  const handleBackdropClick = (e: React.MouseEvent): void => {
    if (e.target === e.currentTarget) persistAndClose()
  }

  const bookingUrl = existingBookingUrl()
  const handleExistingBookingClick = (): void => {
    persistAndClose()
    window.location.href = bookingUrl
  }

  if (!isOpen) return null

  return (
    <div
      className={cn(
        // z-[1200] clears SiteNav (z-[80]) and the mobile NavSheet (z-[120]).
        // GSE-12325 QA reported the Rails header covered the modal top.
        'snplay:fixed snplay:inset-0 snplay:z-[1200] snplay:flex snplay:items-center',
        'snplay:justify-center snplay:bg-black/50 snplay:p-4',
      )}
      onClick={handleBackdropClick}
      data-testid="shipgo-modal-root"
    >
      <div
        className={cn(
          'snplay:bg-white snplay:rounded-2xl snplay:shadow-[0_8px_16px_rgba(0,0,0,0.1)]',
          'snplay:w-full snplay:max-w-[480px] snplay:relative',
          'snplay:pt-12 snplay:pb-8 snplay:px-8 snplay:font-poppins',
          // iPhone 14 (390x664) can't fit the whole panel: cap to viewport
          // minus outer padding and let the body scroll instead of letting
          // the top clip off-screen (GSE-12325 QA finding).
          'snplay:max-h-[calc(100dvh-32px)] snplay:overflow-y-auto',
        )}
      >
        <button
          type="button"
          onClick={persistAndClose}
          aria-label="Close"
          data-testid="shipgo-modal-close"
          className={cn(
            'snplay:absolute snplay:top-2 snplay:right-2',
            'snplay:flex snplay:items-center snplay:justify-center',
            'snplay:w-11 snplay:h-11 snplay:rounded-lg snplay:bg-white',
            'snplay:text-[#1C2938]',
          )}
        >
          <XIcon width={20} height={20} />
        </button>

        <div data-testid="shipgo-modal-heading">
          <p
            className={cn(
              'snplay:font-bold snplay:text-[22px] snplay:leading-[28px]',
              'snplay:sm:text-[28px] snplay:sm:leading-[32px]',
              'snplay:text-[#1C2938]',
            )}
          >
            Looking for ShipGo?
          </p>
          <p
            className={cn(
              'snplay:mt-2 snplay:font-bold',
              'snplay:text-[30px] snplay:leading-[36px]',
              'snplay:sm:text-[40px] snplay:sm:leading-[44px]',
              'snplay:text-[#02A40B]',
            )}
          >
            You&rsquo;re in the right place.
          </p>
        </div>

        <div
          className={cn(
            'snplay:mt-8 snplay:text-[16px] snplay:leading-[28px]',
            'snplay:text-[#1C2938]',
          )}
        >
          <p className="snplay:font-semibold">
            Your favorite luggage shipping service now has a new home. Welcome to Ship&amp;Play.
          </p>
          <p className="snplay:mt-4">
            Ship&amp;Play offers everything you loved about ShipGo, including:
          </p>
          <ul className="snplay:list-disc snplay:pl-6 snplay:mt-2">
            <li>Door-to-door delivery</li>
            <li>Easy tracking</li>
            <li>The same knowledgeable, friendly support team</li>
          </ul>
        </div>

        <button
          type="button"
          onClick={persistAndClose}
          data-testid="shipgo-modal-cta"
          className={cn(
            'snplay:mt-8 snplay:w-full snplay:h-14 snplay:rounded-lg',
            'snplay:font-semibold snplay:text-[16px] snplay:leading-[28px]',
            'snplay:bg-[#48C44F] snplay:text-[#1C2938]',
          )}
        >
          Start Shipping
        </button>

        {bookingUrl && (
          <>
            <p
              className={cn(
                'snplay:mt-8 snplay:text-center snplay:font-semibold',
                'snplay:text-[14px] snplay:leading-[24px] snplay:text-[#1C2938]',
              )}
              data-testid="shipgo-modal-fineprint"
            >
              Have an existing ShipGo booking?
            </p>
            <button
              type="button"
              onClick={handleExistingBookingClick}
              data-testid="shipgo-modal-existing-booking"
              className={cn(
                'snplay:mt-2 snplay:w-full snplay:h-14 snplay:rounded-lg',
                'snplay:font-semibold snplay:text-[16px] snplay:leading-[28px]',
                'snplay:bg-white snplay:border-2',
                'snplay:text-[#1C2938] snplay:border-[#1C2938]',
              )}
            >
              Access It Here
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default ShipGoWelcomeModal
